import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Home, Plus, RefreshCw, ShoppingBasket, Tag, Trash2, Users, Wallet, XCircle } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../context/AuthContext';
import { useActingAs } from '../context/ActingAsContext';
import managedAccountsService from '../services/managedAccountsService';
import SubscribeOnBehalfDialog from '../components/admin/SubscribeOnBehalfDialog';
import AutoLoginOnBehalfDialog from '../components/admin/AutoLoginOnBehalfDialog';
import EditCapitalDialog from '../components/admin/EditCapitalDialog';
import EditGroupDialog from '../components/admin/EditGroupDialog';
import { ManagedAccount } from '../types';

function formatIST(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return '—';
  }
}

function formatCapital(value: number | null): string {
  if (value == null) return '—';
  return `₹${value.toLocaleString('en-IN')}`;
}

const AdminManagedUsersPage: React.FC = () => {
  const { user, isAccountManager, isSuperAdmin } = useAuth();
  const { refresh: refreshActingAsPool } = useActingAs();
  const navigate = useNavigate();
  const toast = useToast();

  const [accounts, setAccounts] = useState<ManagedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [attachOpen, setAttachOpen] = useState(false);
  const [attachClientId, setAttachClientId] = useState('');
  const [attachError, setAttachError] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);

  const [pendingDetach, setPendingDetach] = useState<ManagedAccount | null>(null);
  const [isDetaching, setIsDetaching] = useState(false);

  const [subscribingFor, setSubscribingFor] = useState<ManagedAccount | null>(null);
  const [autoLoginFor, setAutoLoginFor] = useState<ManagedAccount | null>(null);
  const [capitalFor, setCapitalFor] = useState<ManagedAccount | null>(null);
  const [groupFor, setGroupFor] = useState<ManagedAccount | null>(null);

  // Redirect non-permitted users to the dashboard. Defensive — the sidebar
  // link only shows for permitted users, but a direct URL hit needs the gate.
  useEffect(() => {
    if (!isAccountManager) {
      navigate('/', { replace: true });
    }
  }, [isAccountManager, navigate]);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await managedAccountsService.list();
      setAccounts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load managed accounts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleAttach = async () => {
    const clientId = attachClientId.trim();
    if (!clientId) {
      setAttachError('Zebu Client ID is required');
      return;
    }
    // Phase 6: attach is SuperAdmin-only. The dialog only renders for
    // SuperAdmins (see actions block above) but defensively guard here too.
    if (!isSuperAdmin || !user?.id) {
      setAttachError('Only SuperAdmins can attach managed users.');
      return;
    }
    setIsAttaching(true);
    setAttachError(null);
    try {
      // From this page, the SuperAdmin is attaching to their OWN AM pool
      // (they manage themselves). For attaching to another AM's pool,
      // SuperAdmin uses /admin/super/managed-users.
      await managedAccountsService.attach(user.id, clientId);
      toast.showSuccess(`Attached ${clientId} to your managed pool`);
      setAttachOpen(false);
      setAttachClientId('');
      // allSettled — attach already succeeded; a follow-up pool refresh
      // failure must not show an error toast that contradicts the success
      // toast and tricks the admin into retrying the attach.
      await Promise.allSettled([fetchAccounts(), refreshActingAsPool()]);
    } catch (err: any) {
      setAttachError(err.message || 'Failed to attach account');
    } finally {
      setIsAttaching(false);
    }
  };

  const handleDetach = async () => {
    if (!pendingDetach) return;
    setIsDetaching(true);
    try {
      await managedAccountsService.detach(pendingDetach.managed_user_id, pendingDetach.managed_client_id);
      toast.showSuccess(`Detached ${pendingDetach.managed_client_id}`);
      setPendingDetach(null);
      // allSettled — see comment above the attach refresh.
      await Promise.allSettled([fetchAccounts(), refreshActingAsPool()]);
    } catch (err: any) {
      toast.showError(err.message || 'Failed to detach account');
    } finally {
      setIsDetaching(false);
    }
  };

  const autoLoginOnCount = accounts.filter(a => a.auto_login_enabled).length;

  if (!isAccountManager) {
    return null; // useEffect redirects; render nothing in the interim
  }

  return (
    <StandardLayout>
      <PageHeader
        title="Managed Users"
        pageType="management"
        icon={<Users className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Account Manager' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={fetchAccounts}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              size="sm"
              disabled={isLoading}
            >
              Refresh
            </Button>
            {/* Phase 6 hard-switched POST /admin/managed-users to SuperAdmins-only.
                AccountManagers can no longer self-attach — pool assignment is
                done by a SuperAdmin via /admin/super/managed-users. We keep the
                button visible (and functional) for SuperAdmins who want to
                attach directly from this page in addition to their dedicated
                management screen. */}
            {isSuperAdmin && (
              <Button
                onClick={() => { setAttachClientId(''); setAttachError(null); setAttachOpen(true); }}
                leftIcon={<Plus className="h-4 w-4" />}
                size="sm"
              >
                Attach Account
              </Button>
            )}
          </div>
        }
      />

      {/* AccountManager-without-SuperAdmin explanation */}
      {isAccountManager && !isSuperAdmin && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
          A SuperAdmin assigns managed-user pools to your account. To get a new
          user added, ask your SuperAdmin to attach them via the Publisher
          Groups admin tools.
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Managed</div>
          <div className="text-xl font-bold mt-1 text-blue-600 dark:text-blue-400 tabular-nums">{accounts.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Auto-Login On</div>
          <div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400 tabular-nums">{autoLoginOnCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Auto-Login Off</div>
          <div className="text-xl font-bold mt-1 text-gray-500 dark:text-gray-400 tabular-nums">{accounts.length - autoLoginOnCount}</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Managed Accounts ({accounts.length})</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p>No managed accounts yet.</p>
              <p className="text-sm mt-2">Ask your user to sign in once via Zebu OAuth, then click Attach Account above.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name / Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Client ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Capital</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Group</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Auto-Login</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Login</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Attached</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {accounts.map((a) => (
                    <tr key={`${a.managed_user_id}#${a.managed_client_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{a.full_name || '(unnamed)'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{a.email || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">{a.managed_client_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white tabular-nums">{formatCapital(a.capital)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {a.group_name ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: a.group_color || '#9ca3af' }}
                            />
                            <span className="text-gray-900 dark:text-white">{a.group_name}</span>
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Badge variant={a.auto_login_enabled ? 'success' : 'default'} size="sm">
                          {a.auto_login_enabled ? 'ON' : 'OFF'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatIST(a.last_oauth_login)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatIST(a.granted_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAutoLoginFor(a)}
                            leftIcon={<Bot className="w-3.5 h-3.5" />}
                          >
                            {a.auto_login_enabled ? 'Update Credentials' : 'Enable Auto-Login'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCapitalFor(a)}
                            leftIcon={<Wallet className="w-3.5 h-3.5" />}
                          >
                            Set Capital
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setGroupFor(a)}
                            leftIcon={<Tag className="w-3.5 h-3.5" />}
                          >
                            Set Group
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setSubscribingFor(a)}
                            leftIcon={<ShoppingBasket className="w-3.5 h-3.5" />}
                          >
                            Subscribe to Basket
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPendingDetach(a)}
                            leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                          >
                            Detach
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attach dialog */}
      <Modal isOpen={attachOpen} onClose={() => setAttachOpen(false)} maxWidth="md" ariaLabel="Attach Managed Account">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Attach Managed Account</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Paste the Zebu Client ID of an existing user who has already signed into the platform at least once via Zebu OAuth.
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Zebu Client ID</label>
            <Input
              value={attachClientId}
              onChange={(e) => { setAttachClientId(e.target.value); setAttachError(null); }}
              placeholder="e.g. Z71517"
              disabled={isAttaching}
            />
            {attachError && <p className="mt-2 text-xs text-red-600">{attachError}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAttachOpen(false)} disabled={isAttaching}>Cancel</Button>
            <Button onClick={handleAttach} isLoading={isAttaching} disabled={isAttaching}>Attach</Button>
          </div>
        </div>
      </Modal>

      {/* Subscribe-on-Behalf dialog (Phase 3) */}
      {subscribingFor && (
        <SubscribeOnBehalfDialog
          open={true}
          managedUserId={subscribingFor.managed_user_id}
          managedClientId={subscribingFor.managed_client_id}
          managedDisplayName={subscribingFor.full_name || subscribingFor.email || subscribingFor.managed_client_id}
          onClose={() => setSubscribingFor(null)}
          onSuccess={() => {
            toast.showSuccess(`Subscribed ${subscribingFor.managed_client_id} to basket`);
            setSubscribingFor(null);
          }}
        />
      )}

      {/* Auto-Login-on-Behalf dialog (Phase 3.6) */}
      {autoLoginFor && (
        <AutoLoginOnBehalfDialog
          open={true}
          managedUserId={autoLoginFor.managed_user_id}
          managedClientId={autoLoginFor.managed_client_id}
          managedDisplayName={autoLoginFor.full_name || autoLoginFor.email || autoLoginFor.managed_client_id}
          currentlyEnabled={autoLoginFor.auto_login_enabled}
          onClose={() => setAutoLoginFor(null)}
          onSuccess={() => {
            toast.showSuccess(`Auto-login configured for ${autoLoginFor.managed_client_id}`);
            setAutoLoginFor(null);
            fetchAccounts();
          }}
        />
      )}

      {/* Edit Capital dialog */}
      {capitalFor && (
        <EditCapitalDialog
          open={true}
          managedUserId={capitalFor.managed_user_id}
          managedClientId={capitalFor.managed_client_id}
          managedDisplayName={capitalFor.full_name || capitalFor.email || capitalFor.managed_client_id}
          currentCapital={capitalFor.capital ?? 0}
          onClose={() => setCapitalFor(null)}
          onSuccess={(newCapital) => {
            toast.showSuccess(`Capital updated to ₹${newCapital.toLocaleString('en-IN')}`);
            setCapitalFor(null);
            fetchAccounts();
          }}
        />
      )}

      {/* Edit Client Group dialog (Phase 2) */}
      {groupFor && (
        <EditGroupDialog
          open={true}
          managedUserId={groupFor.managed_user_id}
          managedClientId={groupFor.managed_client_id}
          managedDisplayName={groupFor.full_name || groupFor.email || groupFor.managed_client_id}
          currentGroupId={groupFor.group_id || null}
          currentGroupName={groupFor.group_name}
          onClose={() => setGroupFor(null)}
          onSuccess={(_newGroupId, newGroupName) => {
            toast.showSuccess(
              newGroupName ? `Group set to "${newGroupName}"` : 'Group cleared',
            );
            setGroupFor(null);
            fetchAccounts();
          }}
        />
      )}

      {/* Detach confirm */}
      <ConfirmDialog
        isOpen={pendingDetach !== null}
        onClose={() => !isDetaching && setPendingDetach(null)}
        onConfirm={handleDetach}
        title="Detach Managed Account"
        message={
          pendingDetach
            ? `Are you sure you want to detach ${pendingDetach.managed_client_id} (${pendingDetach.full_name || pendingDetach.email}) from your pool? The user's own broker account is unaffected.`
            : ''
        }
        confirmText={isDetaching ? 'Detaching…' : 'Detach'}
        cancelText="Cancel"
        variant="danger"
        icon={<Trash2 className="h-6 w-6 text-red-600" />}
      />
    </StandardLayout>
  );
};

export default AdminManagedUsersPage;
